'use client';

import { useFormatter, useTranslations } from '@ifixit/i18n/hooks';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import type { ReactNode } from 'react';
import type { TrustedShopsRating } from '../trusted-shops-rating';

/**
 * Our copy of Trusted Shops' collapsed Trustbadge, drawn from data fetched on
 * our servers. Unlike their widget it loads nothing from Trusted Shops in the
 * browser, so it needs no cookie consent.
 */
export function TrustedShopsRatingBadge({
   rating,
   seal,
   theme,
   stacked = false,
   className,
}: {
   rating: TrustedShopsRating;
   /**
    * The 24px trustmark from ../assets/trustmark.svg. Passed in because Next.js
    * imports an SVG as an image URL and Carpenter's webpack as a component.
    */
   seal: ReactNode;
   theme: 'dark' | 'light';
   /** Always two lines, for narrow spots like the product column. */
   stacked?: boolean;
   className?: string;
}) {
   const t = useTranslations('TrustedShops');
   const format = useFormatter();
   const mark = format.number(rating.mark, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
   const grade = t(`grade.${rating.markDescription}`);

   return (
      <a
         href={rating.profileUrl}
         target="_blank"
         rel="noopener"
         aria-label={t('label', { mark, count: rating.reviewCount, grade })}
         className={cn(
            'inline-flex items-center justify-center gap-x-3 gap-y-1 rounded-md px-3 py-2 text-sm hover:no-underline',
            stacked ? 'flex-col' : 'flex-wrap',
            theme === 'dark' ? 'text-white' : 'border border-gray-200 bg-white text-gray-900',
            className
         )}
      >
         {/* Two halves that wrap onto two lines on phones, so no divider
             ever starts a line. */}
         <span className="flex items-center gap-3">
            {seal}
            <Stars mark={rating.mark} />
            <span className="font-semibold">{mark}</span>
         </span>
         <Separator className={stacked ? 'hidden' : 'max-sm:hidden'} />
         <span className="flex items-center gap-3">
            <span>{t('reviewCount', { count: rating.reviewCount })}</span>
            <Separator />
            <span className="font-semibold">{grade}</span>
         </span>
      </a>
   );
}

// Each star fills in proportion, so 4.71 draws four full stars and one 71%
// star, the way Trusted Shops draws it. #db6b22 is their star orange.
function Stars({ mark }: { mark: number }) {
   return (
      <span className="flex gap-0.5" aria-hidden>
         {[0, 1, 2, 3, 4].map(index => {
            const fill = Math.min(Math.max(mark - index, 0), 1);
            return (
               <span key={index} className="relative flex">
                  <FaIcon name="star" className="h-4 w-auto aspect-[576/512] text-gray-300" />
                  <span
                     className="absolute inset-0 overflow-x-clip"
                     style={{ width: `${fill * 100}%` }}
                  >
                     <FaIcon name="star" className="h-4 w-auto aspect-[576/512] text-[#db6b22]" />
                  </span>
               </span>
            );
         })}
      </span>
   );
}

function Separator({ className }: { className?: string }) {
   return <span aria-hidden className={cn('h-4 w-px bg-current opacity-40', className)} />;
}
